import { addToast, Button, ModalBody, ModalContent, ModalFooter, ModalHeader, Card, CardBody, Progress, Spinner } from "@heroui/react";
import { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { TRequest } from "@shared/entities";
import { DocumentTypeSchema, TDocumentType, TDocument } from "@shared/entities/document";
import { firebaseApi } from "src/firebase-api";
import { getDownloadURL, type UploadTask } from "firebase/storage";
import { TClientDocuments, TClientDocumentType } from "src/utils/clientDocuments";

const DocumentUpload: React.FC<{
	label: string;
	description: string;
	folder: string;
	icon: string;
	type: TDocumentType;
	onChange: (file: TDocument) => void;
	onRemove: (file: TDocument) => Promise<void>;
	/** True from file selection until the document is in the dialog (or the
	 *  upload failed) — the dialog must not approve in between. */
	onUploadingChange: (uploading: boolean) => void;
	/** Approving or cancelling: no new file may join a set the dialog is
	 *  already saving or discarding. */
	isLocked: boolean;
	value?: TDocument | null;
}> = ({ label, description, icon, onChange, onRemove, onUploadingChange, isLocked, value, type, folder }) => {
	const [isUploading, setIsUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [isRemoving, setIsRemoving] = useState(false);
	// The dialog can close mid-upload (cancel, X, backdrop). Nothing would ever
	// point at a file that finishes after that, so the upload is cancelled, and
	// a file that still lands is deleted.
	const task = useRef<UploadTask | null>(null);
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
			task.current?.cancel();
		};
	}, []);

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file && !isLocked) {
			setIsUploading(true);
			onUploadingChange(true);
			setUploadProgress(0);

			// Lower-case: the document check accepts "pdf", not "PDF".
			const extension = file.name?.split(".").at(-1)?.toLowerCase();
			const name = file.name;
			// Unique object name: a file with the same name as one already saved
			// must not overwrite it in Storage before the admin approves.
			const storageName = `${Date.now()}-${name}`;

			const upload = firebaseApi.storage.uploadFileResumable({
				file,
				folder: folder,
				name: storageName,
			});
			task.current = upload;

			upload.on(
				"state_changed",
				(snapshot) => {
					const progressPercent = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
					setUploadProgress(Math.round(progressPercent));
				},
				(err) => {
					task.current = null;
					if (!mounted.current) return; // cancelled because the dialog closed
					console.error("Upload error:", err.message);
					setIsUploading(false);
					onUploadingChange(false);
					addToast({ title: "העלאת הקובץ נכשלה", description: label, color: "danger" });
				},
				async () => {
					task.current = null;
					const path = `${folder}/${storageName}`;
					let downloadURL: string;
					try {
						downloadURL = await getDownloadURL(upload.snapshot.ref);
					} catch (err) {
						if (!mounted.current) {
							await firebaseApi.storage.remove(path);
							return;
						}
						console.error("Upload error:", err);
						setIsUploading(false);
						onUploadingChange(false);
						addToast({ title: "העלאת הקובץ נכשלה", description: label, color: "danger" });
						return;
					}
					if (!mounted.current) {
						// Landed after the dialog closed: unreferenced, so it goes.
						await firebaseApi.storage.remove(path);
						return;
					}
					setIsUploading(false);

					const document: TDocument = {
						createdAt: Date.now(),
						extension: extension as TDocument["extension"],
						fileType: upload.snapshot.metadata.contentType as TDocument["fileType"],
						type: type,
						name: name,
						size: upload.snapshot.metadata.size,
						url: downloadURL,
						path,
						label: file?.name ?? "",
					};

					// Kept in the dialog only. It reaches the request — and the
					// client — when the admin presses "אישור".
					onChange(document);
					onUploadingChange(false);
				}
			);
		}
	};

	const handleRemoveFile = async () => {
		if (!value || isLocked) return;
		setIsRemoving(true);
		try {
			await onRemove(value);
		} finally {
			setIsRemoving(false);
		}
	};

	return (
		<Card
			className={`border ${
				isUploading ? "border-primary" : value ? "border-success" : "border-default-200"
			} hover:border-primary transition-colors`}
			shadow="sm"
		>
			<CardBody className="p-3">
				<div className="flex items-start gap-3">
					<div
						className={`flex-shrink-0 p-2 rounded-lg ${
							isUploading
								? "bg-primary-100"
								: value
								? "bg-success-100"
								: "bg-default-100"
						}`}
					>
						{isUploading ? (
							<Spinner size="sm" color="primary" />
						) : (
							<Icon
								icon={value ? "lucide:file-check" : icon}
								className={`h-5 w-5 ${
									value ? "text-success" : "text-default-600"
								}`}
							/>
						)}
					</div>
					<div className="flex-1 min-w-0">
						<div className="flex items-start justify-between gap-2 mb-1">
							<div className="flex-1 min-w-0">
								<h4 className="font-medium text-sm">{label}</h4>
								<p className="text-xs text-default-500 mt-0.5">{description}</p>
							</div>
							{value && !isUploading && (
								<Button
									type="button"
									isIconOnly
									size="sm"
									variant="light"
									color="danger"
									isLoading={isRemoving}
									onPress={handleRemoveFile}
									isDisabled={isRemoving || isLocked}
									className="flex-shrink-0"
								>
									<Icon icon="lucide:x" className="h-4 w-4" />
								</Button>
							)}
						</div>

						{isUploading && (
							<div className="w-full mt-2">
								<Progress
									aria-label="Uploading..."
									value={uploadProgress}
									color="primary"
									size="sm"
									showValueLabel={true}
								/>
							</div>
						)}

						{value && !isUploading ? (
							<div className="flex items-center gap-2 mt-2 p-2 bg-default-50 rounded-md">
								<Icon icon="lucide:file" className="h-4 w-4 text-success flex-shrink-0" />
								<span className="text-xs truncate flex-1 text-default-700">
									{value?.label ?? value.name}
								</span>
							</div>
						) : !isUploading ? (
							<div className="mt-2">
								<label className={isLocked ? "cursor-not-allowed" : "cursor-pointer"}>
									<Button
										size="sm"
										variant="flat"
										color="primary"
										startContent={<Icon icon="lucide:upload" className="h-4 w-4" />}
										className="w-full"
										as="div"
										isDisabled={isLocked}
									>
										בחר קובץ
									</Button>
									<input
										type="file"
										className="hidden"
										accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.webp"
										onChange={handleFileChange}
										disabled={isUploading || isRemoving || isLocked}
									/>
								</label>
								<p className="text-xs text-default-400 mt-1.5 text-center">
									PDF, JPG, PNG, DOC, DOCX, WEBP • עד 2MB
								</p>
							</div>
						) : null}
					</div>
				</div>
			</CardBody>
		</Card>
	);
};

export function AdminApproveRequestModal({
	onSubmit,
	request,
}: {
	/** Saves the request as approved together with these documents and reports
	 *  the outcome to the admin. Resolves true once the request is saved — even
	 *  if something after that failed — and the dialog then closes itself. It
	 *  must not close the dialog: the dialog has to mark the save first, or its
	 *  close-time cleanup would treat the just-approved files as unsaved. */
	onSubmit: (documentsForClient: TClientDocuments) => Promise<boolean>;
	/** Read fresh when the dialog opens — its saved documents are the baseline
	 *  that tells a kept file from a newly uploaded one. */
	request: TRequest;
}) {
	const saved: TClientDocuments = request.documentsForClient ?? {};
	const [documentsForClient, setDocumentsForClient] = useState<TClientDocuments>(saved);
	const [submitting, setSubmitting] = useState(false);
	// Per type, so two uploads at once are both waited for.
	const [uploading, setUploading] = useState<Partial<Record<TClientDocumentType, boolean>>>({});
	const isUploading = Object.values(uploading).some(Boolean);
	// Set once the request holds these files. From then on the dialog never
	// deletes anything from Storage — even if it is still open (a refresh after
	// the save failed) and the admin presses "ביטול".
	const committed = useRef(false);
	// Approve and cancel must never overlap: cancel deletes the unsaved files
	// that an approval would point the request at. A ref, not state — a second
	// click can arrive before a state update has re-rendered the buttons.
	const busy = useRef<"submit" | "cancel" | null>(null);
	const [cancelling, setCancelling] = useState(false);
	// Latest documents, for the cleanup that runs when the dialog goes away.
	const latest = useRef(documentsForClient);
	latest.current = documentsForClient;
	const deleted = useRef(new Set<string>());
	// Closed (X / backdrop / Escape) while an approval was still running: the
	// cleanup has to wait for its outcome — see handleApprove.
	const dismissedMidApproval = useRef(false);

	const clientDocumentFields: Array<{
		type: TClientDocumentType;
		label: string;
		description: string;
		icon: string;
	}> = [
		{
			type: DocumentTypeSchema.Values.CANNABIS_LICENSE,
			label: "רישיון קנאביס",
			description: "רישיון קנאביס רפואי",
			icon: "lucide:file-check",
		},
		{
			type: DocumentTypeSchema.Values.PRESCRIPTIONS,
			label: "מרשמים",
			description: "מרשמים רפואיים",
			icon: "lucide:file-text",
		},
		{
			type: DocumentTypeSchema.Values.DOCTOR_RECOMMENDATION,
			label: "המלצת רופא",
			description: "המלצת רופא ללקוח",
			icon: "lucide:file-heart",
		},
	];

	/** A file uploaded in this dialog and not yet saved: nothing else points at it. */
	const isUnsavedUpload = (type: TClientDocumentType, document: TDocument) =>
		!committed.current && document.path !== saved[type]?.path;

	const handleRemove = async (type: TClientDocumentType, document: TDocument) => {
		if (busy.current) return;
		// Out of the dialog first, so an approval can no longer include it; then
		// an unsaved upload can go from Storage. A file saved before stays
		// there: approving without it only detaches it from the request.
		setDocumentsForClient((prev) => ({ ...prev, [type]: null }));
		if (isUnsavedUpload(type, document) && document.path) {
			await firebaseApi.storage.remove(document.path);
		}
	};

	/** Delete every upload that is in the dialog but was never saved. Safe to
	 *  call twice (cancel, then the unmount below) and a no-op once approved. */
	const discardUnsavedUploads = () => {
		const paths = clientDocumentFields
			.map(({ type }) => ({ type, document: latest.current[type] }))
			.filter(({ type, document }) => document?.path && isUnsavedUpload(type, document))
			.map(({ document }) => document!.path)
			.filter((path) => !deleted.current.has(path));
		paths.forEach((path) => deleted.current.add(path));
		return Promise.all(paths.map((path) => firebaseApi.storage.remove(path)));
	};

	// Closing with X, the backdrop or Escape skips "ביטול" — clean up the same way.
	// Mid-approval the files may be about to land on the request, so the
	// decision waits for the outcome (handleApprove).
	useEffect(
		() => () => {
			if (busy.current === "submit") dismissedMidApproval.current = true;
			else void discardUnsavedUploads();
		},
		[], // eslint-disable-line react-hooks/exhaustive-deps
	);

	const handleCancel = async (onClose: () => void) => {
		if (busy.current) return;
		busy.current = "cancel";
		setCancelling(true);
		try {
			await discardUnsavedUploads();
		} finally {
			// The dialog closes either way; `busy` stays set so nothing can be
			// approved from it in the meantime.
			onClose();
		}
	};

	const handleApprove = async (onClose: () => void) => {
		if (busy.current || isUploading) return;
		busy.current = "submit";
		setSubmitting(true);
		let saved = false;
		try {
			saved = await onSubmit(documentsForClient);
		} finally {
			if (saved) committed.current = true; // before closing — see onSubmit
			busy.current = null;
			setSubmitting(false);
		}
		if (saved) {
			if (!dismissedMidApproval.current) onClose();
		} else if (dismissedMidApproval.current) {
			// Closed while approving, and the approval failed: nothing will ever
			// point at these uploads, and there is no dialog left to remove them.
			void discardUnsavedUploads();
		}
	};

	return (
		<ModalContent className="max-w-2xl">
			{(onClose) => (
				<>
					<ModalHeader className="flex flex-col gap-1">
						אישור בקשה
						<p className="text-sm font-normal text-default-500">
							ניתן להוסיף מסמכים ללקוח (אופציונלי). הלקוח יראה אותם רק אחרי לחיצה על "אישור".
						</p>
					</ModalHeader>
					<ModalBody className="py-4">
						<div className="space-y-3">
							{clientDocumentFields.map((field) => (
								<DocumentUpload
									key={field.type}
									type={field.type as TDocumentType}
									label={field.label}
									description={field.description}
									icon={field.icon}
									folder={`requests/${request.id}/documentsForClient`}
									onChange={(doc) =>
										setDocumentsForClient((prev) => ({ ...prev, [field.type]: doc }))
									}
									onRemove={(doc) => handleRemove(field.type, doc)}
									onUploadingChange={(value) =>
										setUploading((prev) => ({ ...prev, [field.type]: value }))
									}
									isLocked={submitting || cancelling}
									value={documentsForClient[field.type] ?? null}
								/>
							))}
						</div>
					</ModalBody>
					<ModalFooter>
						<Button
							variant="light"
							isLoading={cancelling}
							isDisabled={submitting || cancelling}
							onPress={() => handleCancel(onClose)}
						>
							ביטול
						</Button>
						<Button
							type="submit"
							color="primary"
							isLoading={submitting}
							isDisabled={isUploading || cancelling}
							onPress={() => handleApprove(onClose)}
						>
							{isUploading ? "ממתין לסיום ההעלאה…" : "אישור"}
						</Button>
					</ModalFooter>
				</>
			)}
		</ModalContent>
	);
}
